import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';

const MAX_SHEETS = 50;

const REQUIRED_COLUMNS = ['course_name', 'category', 'course_type', 'level', 'duration'];

const COLUMN_ALIASES = {
  'course name': 'course_name',
  'coursename': 'course_name',
  'name': 'course_name',
  'course type': 'course_type',
  'coursetype': 'course_type',
  'type': 'course_type',
  'specialization': 'specialization',
  'specialisation': 'specialization',
  'fees': 'fees',
  'fee': 'fees',
  'show fee': 'show_fee',
  'showfee': 'show_fee',
  'eligibility': 'eligibility',
  'admission process': 'admission_process',
  'admissionprocess': 'admission_process',
  'placement stats': 'placement_stats',
  'placementstats': 'placement_stats',
  'accreditation': 'accreditation',
};

function normalizeHeader(raw) {
  const lower = raw?.trim()?.toLowerCase()?.replace(/\s+/g, ' ');
  return COLUMN_ALIASES?.[lower] || lower?.replace(/\s+/g, '_');
}

/**
 * Parse an Excel file and return sheet data
 * @param {File} file
 * @returns {{ sheetNames: string[], sheets: Record<string, object[]> }}
 */
export function parseExcelFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        const sheetNames = workbook.SheetNames.slice(0, MAX_SHEETS);
        const sheets = {};

        sheetNames.forEach((name) => {
          const ws = workbook.Sheets[name];
          const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
          // Normalize headers
          const normalized = rows.map((row) => {
            const newRow = {};
            Object.keys(row).forEach((key) => {
              newRow[normalizeHeader(key)] = row[key];
            });
            return newRow;
          });
          sheets[name] = normalized;
        });

        resolve({ sheetNames, sheets, totalSheets: workbook.SheetNames.length });
      } catch (err) {
        reject(new Error(`Failed to parse Excel file: ${err?.message}`));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Validate a single sheet's rows
 */
function validateSheetRows(rows, sheetName, validCategories, validLevels) {
  const errors = [];
  const validRows = [];

  if (!rows || rows?.length === 0) {
    errors?.push({
      sheet: sheetName,
      row: null,
      column: null,
      value: null,
      message: 'Sheet is empty — no data rows found',
    });
    return { errors, validRows };
  }

  const headers = Object.keys(rows?.[0] || {});
  const missingHeaders = REQUIRED_COLUMNS?.filter((f) => !headers?.includes(f));
  if (missingHeaders?.length > 0) {
    errors?.push({
      sheet: sheetName,
      row: null,
      column: null,
      value: null,
      message: `Missing required columns: ${missingHeaders?.join(', ')}`,
    });
    return { errors, validRows };
  }

  rows?.forEach((row, index) => {
    const rowNumber = index + 2; // row 1 = header
    const rowErrors = [];

    // Required fields
    REQUIRED_COLUMNS?.forEach((field) => {
      const val = String(row?.[field] ?? '')?.trim();
      if (!val) {
        rowErrors?.push({
          sheet: sheetName,
          row: rowNumber,
          column: field,
          value: '',
          message: `Column "${field?.replace(/_/g, ' ')}" is required but empty`,
        });
      }
    });

    // Validate category
    const rawCategory = String(row?.category ?? '')?.trim();
    if (rawCategory) {
      const norm = rawCategory?.toLowerCase();
      if (validCategories?.length > 0 && !validCategories?.includes(norm)) {
        rowErrors?.push({
          sheet: sheetName,
          row: rowNumber,
          column: 'category',
          value: rawCategory,
          message: `"${rawCategory}" is not a valid category`,
        });
      }
    }

    // Validate level
    const rawLevel = String(row?.level ?? '')?.trim();
    if (rawLevel) {
      const norm = rawLevel?.toLowerCase();
      if (validLevels?.length > 0 && !validLevels?.includes(norm)) {
        rowErrors?.push({
          sheet: sheetName,
          row: rowNumber,
          column: 'level',
          value: rawLevel,
          message: `"${rawLevel}" is not a valid level`,
        });
      }
    }

    // Validate fees
    const rawFees = String(row?.fees ?? '')?.trim();
    if (rawFees) {
      const feeNum = parseFloat(rawFees);
      if (isNaN(feeNum) || feeNum < 0) {
        rowErrors?.push({
          sheet: sheetName,
          row: rowNumber,
          column: 'fees',
          value: rawFees,
          message: `"${rawFees}" is not a valid fee amount (must be a positive number)`,
        });
      }
    }

    // Validate show_fee
    const rawShowFee = String(row?.show_fee ?? '')?.trim()?.toLowerCase();
    if (rawShowFee && !['true', 'false', '1', '0', 'yes', 'no', '']?.includes(rawShowFee)) {
      rowErrors?.push({
        sheet: sheetName,
        row: rowNumber,
        column: 'show_fee',
        value: row?.show_fee,
        message: `"${row?.show_fee}" is invalid — show_fee must be true/false`,
      });
    }

    if (rowErrors?.length > 0) {
      errors?.push(...rowErrors);
    } else {
      validRows?.push({ ...row, _rowNumber: rowNumber });
    }
  });

  return { errors, validRows };
}

/**
 * Look up institute by name (exact match, case-insensitive)
 */
async function findInstituteByName(name) {
  const { data, error } = await supabase?.from('institute_profiles')?.select('id, institute_name')?.ilike('institute_name', name?.trim())?.limit(1);

  if (error) throw error;
  return data?.[0] || null;
}

/**
 * Insert a single course row for an institute
 */
async function insertCourse(instituteId, row) {
  const showFeeRaw = String(row?.show_fee ?? '')?.trim()?.toLowerCase();
  const showFee = !['false', '0', 'no']?.includes(showFeeRaw);

  const payload = {
    institute_id: instituteId,
    course_name: String(row?.course_name ?? '')?.trim(),
    category: String(row?.category ?? '')?.trim()?.toLowerCase(),
    course_type: String(row?.course_type ?? '')?.trim(),
    specialization: String(row?.specialization ?? '')?.trim() || null,
    level: String(row?.level ?? '')?.trim()?.toLowerCase(),
    duration: String(row?.duration ?? '')?.trim(),
    fees: row?.fees ? parseFloat(String(row?.fees)?.trim()) : null,
    show_fee: showFee,
    eligibility: String(row?.eligibility ?? '')?.trim() || null,
    admission_process: String(row?.admission_process ?? '')?.trim() || null,
    placement_stats: String(row?.placement_stats ?? '')?.trim() || null,
    accreditation: String(row?.accreditation ?? '')?.trim() || null,
  };

  const { error } = await supabase?.from('courses')?.insert(payload);
  if (error) throw error;
}

/**
 * Main upload function — processes all sheets
 * @param {File} file
 * @param {{ onProgress?: Function }} options
 * @returns {Promise<UploadResult>}
 */
export async function bulkExcelCourseUpload(file, { onProgress } = {}) {
  const allErrors = [];
  const summary = {
    totalSheets: 0,
    processedSheets: 0,
    skippedSheets: 0,
    totalRowsAttempted: 0,
    totalRowsUploaded: 0,
    totalRowsFailed: 0,
    sheetResults: [],
  };

  // 1. Fetch valid catalog values
  let validCategories = [];
  let validLevels = [];
  try {
    const { data } = await supabase?.from('global_course_catalog')?.select('name, type')?.eq('is_active', true);
    validCategories = (data || [])?.filter((d) => d?.type === 'category')?.map((d) => d?.name?.toLowerCase()?.trim());
    validLevels = (data || [])?.filter((d) => d?.type === 'level')?.map((d) => d?.name?.toLowerCase()?.trim());
  } catch (_) {
    // proceed without catalog validation if table unavailable
  }

  // 2. Parse Excel
  let parsed;
  try {
    parsed = await parseExcelFile(file);
  } catch (err) {
    return { success: false, fatalError: err?.message, summary, errors: [] };
  }

  const { sheetNames, sheets, totalSheets } = parsed;
  summary.totalSheets = totalSheets;

  if (sheetNames?.length === 0) {
    return { success: false, fatalError: 'No sheets found in the Excel file.', summary, errors: [] };
  }

  // 3. Process each sheet
  for (let si = 0; si < sheetNames?.length; si++) {
    const sheetName = sheetNames?.[si];
    const rows = sheets?.[sheetName] || [];

    onProgress?.({
      phase: 'validating',
      sheet: sheetName,
      sheetIndex: si + 1,
      totalSheets: sheetNames?.length,
    });

    // 3a. Find institute
    let institute = null;
    try {
      institute = await findInstituteByName(sheetName);
    } catch (err) {
      allErrors?.push({
        sheet: sheetName,
        row: null,
        column: null,
        value: null,
        message: `Database error while looking up institute: ${err?.message}`,
      });
      summary.skippedSheets++;
      summary?.sheetResults?.push({ sheetName, status: 'skipped', reason: 'Institute lookup failed', uploaded: 0, failed: 0 });
      continue;
    }

    if (!institute) {
      allErrors?.push({
        sheet: sheetName,
        row: null,
        column: null,
        value: sheetName,
        message: `No matching institute found for sheet name "${sheetName}"`,
      });
      summary.skippedSheets++;
      summary?.sheetResults?.push({ sheetName, status: 'skipped', reason: 'Institute not found', uploaded: 0, failed: 0 });
      continue;
    }

    // 3b. Validate rows
    const { errors: rowErrors, validRows } = validateSheetRows(rows, sheetName, validCategories, validLevels);
    allErrors?.push(...rowErrors);
    summary.totalRowsAttempted += rows?.length;

    let uploadedCount = 0;
    let failedCount = rowErrors?.length;

    // 3c. Upload valid rows
    for (let ri = 0; ri < validRows?.length; ri++) {
      const row = validRows?.[ri];
      onProgress?.({
        phase: 'uploading',
        sheet: sheetName,
        sheetIndex: si + 1,
        totalSheets: sheetNames?.length,
        rowIndex: ri + 1,
        totalRows: validRows?.length,
      });

      try {
        await insertCourse(institute?.id, row);
        uploadedCount++;
        summary.totalRowsUploaded++;
      } catch (err) {
        const isDuplicate = err?.message?.toLowerCase()?.includes('duplicate') || err?.message?.toLowerCase()?.includes('unique');
        allErrors?.push({
          sheet: sheetName,
          row: row?._rowNumber,
          column: 'course_name',
          value: row?.course_name,
          message: isDuplicate
            ? `Course "${row?.course_name}" already exists for this institute`
            : `Upload failed: ${err?.message}`,
        });
        failedCount++;
        summary.totalRowsFailed++;
      }
    }

    summary.processedSheets++;
    summary?.sheetResults?.push({
      sheetName,
      instituteName: institute?.institute_name,
      status: uploadedCount > 0 ? 'partial' : 'failed',
      uploaded: uploadedCount,
      failed: failedCount,
      total: rows?.length,
    });
  }

  return {
    success: summary?.totalRowsUploaded > 0,
    summary,
    errors: allErrors,
  };
}

/**
 * Generate a downloadable Excel template
 */
export function downloadExcelTemplate() {
  const headers = [
    'course_name',
    'category',
    'course_type',
    'specialization',
    'level',
    'duration',
    'fees',
    'show_fee',
    'eligibility',
    'admission_process',
    'placement_stats',
    'accreditation',
  ];

  const sample = [
    {
      course_name: 'Bachelor of Technology in Computer Science',
      category: 'engineering',
      course_type: 'Full Time',
      specialization: 'Computer Science',
      level: 'undergraduate',
      duration: '4 Years',
      fees: '500000',
      show_fee: 'true',
      eligibility: '10+2 with PCM',
      admission_process: 'JEE Main',
      placement_stats: 'Avg 8 LPA',
      accreditation: 'AICTE',
    },
    {
      course_name: 'Master of Business Administration',
      category: 'business & management',
      course_type: 'Full Time',
      specialization: 'Finance',
      level: 'post graduate',
      duration: '2 Years',
      fees: '800000',
      show_fee: 'true',
      eligibility: 'Bachelor Degree 50%',
      admission_process: 'CAT / MAT',
      placement_stats: 'Avg 12 LPA',
      accreditation: 'AICTE',
    },
  ];

  const ws = XLSX?.utils?.json_to_sheet(sample, { header: headers });
  const wb = XLSX?.utils?.book_new();
  XLSX?.utils?.book_append_sheet(wb, ws, 'Institute Name Here');

  // Add a second example sheet
  const ws2 = XLSX?.utils?.json_to_sheet([], { header: headers });
  XLSX?.utils?.book_append_sheet(wb, ws2, 'Another Institute Name');

  XLSX?.writeFile(wb, 'bulk_course_upload_template.xlsx');
}

/**
 * Generate error report as downloadable Excel
 */
export function downloadErrorReport(errors, summary) {
  const rows = errors?.map((e) => ({
    Sheet: e?.sheet || '',
    Row: e?.row ?? 'N/A',
    Column: e?.column || 'N/A',
    'Cell Value': e?.value ?? '',
    'Error Message': e?.message || '',
  }));

  const ws = XLSX?.utils?.json_to_sheet(rows);
  const wb = XLSX?.utils?.book_new();
  XLSX?.utils?.book_append_sheet(wb, ws, 'Errors');

  // Summary sheet
  const summaryRows = summary?.sheetResults?.map((s) => ({
    'Sheet (Institute)': s?.sheetName,
    'Status': s?.status,
    'Uploaded': s?.uploaded,
    'Failed': s?.failed,
    'Total Rows': s?.total ?? 0,
    'Reason (if skipped)': s?.reason || '',
  }));
  const ws2 = XLSX?.utils?.json_to_sheet(summaryRows);
  XLSX?.utils?.book_append_sheet(wb, ws2, 'Summary');

  XLSX?.writeFile(wb, 'bulk_upload_error_report.xlsx');
}
